import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendRegistrationInterestEmail } from '../services/emailService';
import { toast } from 'react-toastify';
import countryList from 'react-select-country-list';

const RegistrationForm = () => {
  const navigate = useNavigate();

  // Create refs for all required fields
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const addressLine1Ref = useRef(null);
  const cityRef = useRef(null);
  const postcodeRef = useRef(null);
  const countryRef = useRef(null);
  const userTypeRef = useRef(null);
  const areaOfInterestRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    telephone: '',
    userType: '',
    subjectInterest: '',
    addressLine1: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    areaOfInterest: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);

  useEffect(() => {
    const countries = countryList().getData();
    setCountryOptions(countries);
  }, []);

  const userTypeOptions = [
    'Student',
    'Parent',
    'Tutor',
    'School / Tutoring Centre',
    'Corporate / Organisation',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let firstErrorKey = null;

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      if (!firstErrorKey) firstErrorKey = 'firstName';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
      if (!firstErrorKey) firstErrorKey = 'lastName';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
      if (!firstErrorKey) firstErrorKey = 'email';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      if (!firstErrorKey) firstErrorKey = 'email';
    }
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address line is required';
      if (!firstErrorKey) firstErrorKey = 'addressLine1';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City / Town is required';
      if (!firstErrorKey) firstErrorKey = 'city';
    }
    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required';
      if (!firstErrorKey) firstErrorKey = 'postcode';
    }
    if (!formData.country) {
      newErrors.country = 'Please select a country';
      if (!firstErrorKey) firstErrorKey = 'country';
    }
    if (!formData.userType) {
      newErrors.userType = 'Please select an option';
      if (!firstErrorKey) firstErrorKey = 'userType';
    }
    if (!formData.areaOfInterest.trim()) {
      newErrors.areaOfInterest = 'Area of interest is required';
      if (!firstErrorKey) firstErrorKey = 'areaOfInterest';
    }

    return { errors: newErrors, isValid: Object.keys(newErrors).length === 0, firstErrorKey };
  };

  // Improved scroll function using scrollIntoView
  const scrollToField = (ref) => {
    const element = ref.current;
    if (element) {
      // Use scrollIntoView with smooth behavior and offset adjustment
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center', // centers the element in the viewport
      });
      // Focus the element (works for selects and inputs)
      element.focus({ preventScroll: true }); // preventScroll true because we already scrolled
    }
  };

  // Map error keys to refs
  const fieldRefMap = {
    firstName: firstNameRef,
    lastName: lastNameRef,
    email: emailRef,
    addressLine1: addressLine1Ref,
    city: cityRef,
    postcode: postcodeRef,
    country: countryRef,
    userType: userTypeRef,
    areaOfInterest: areaOfInterestRef,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { errors: validationErrors, isValid, firstErrorKey } = validateForm();
    setErrors(validationErrors);

    if (!isValid) {
      // Use requestAnimationFrame to ensure the DOM has updated with error messages
      requestAnimationFrame(() => {
        const targetRef = fieldRefMap[firstErrorKey];
        if (targetRef) {
          // Small additional delay to allow layout shift from error messages
          setTimeout(() => scrollToField(targetRef), 50);
        }
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await sendRegistrationInterestEmail(formData);

      if (result.success) {
        toast.success('Thank you for registering your interest!');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          telephone: '',
          userType: '',
          subjectInterest: '',
          addressLine1: '',
          city: '',
          postcode: '',
          country: 'United Kingdom',
          areaOfInterest: '',
        });
        setErrors({});
        navigate('/');
      } else {
        toast.error('Registration submission failed. Please try again later.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full px-6 sm:px-12 md:px-16 lg:pl-20 lg:pr-12 relative pt-0 pb-8 min-h-[500px]">
      <h2 className="text-left text-4xl md:text-5xl font-black text-[#5c3e21] mb-12 tracking-tight">
        Register Your Interest
      </h2>
      <form onSubmit={handleSubmit} className="w-full" noValidate>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm sm:text-base lg:text-[17px]">
          {/* First Name */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              ref={firstNameRef}
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:border-green-800 bg-white ${
                errors.firstName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your first name"
            />
            {errors.firstName && (
              <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              ref={lastNameRef}
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:border-green-800 bg-white ${
                errors.lastName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your last name"
            />
            {errors.lastName && (
              <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
            )}
          </div>

          {/* Email Address */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              ref={emailRef}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:border-green-800 bg-white ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="you@example.com"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Phone Number - Optional */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Phone Number <span className="text-gray-500 text-sm">(Optional)</span>
            </label>
            <input
              type="tel"
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-800 bg-white"
              placeholder="+1234567890"
            />
          </div>

          {/* Address Line 1 */}
          <div className="md:col-span-2">
            <label className="block text-gray-700 font-semibold mb-2">
              First line of address <span className="text-red-500">*</span>
            </label>
            <input
              ref={addressLine1Ref}
              type="text"
              name="addressLine1"
              value={formData.addressLine1}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:border-green-800 bg-white ${
                errors.addressLine1 ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Street address, P.O. Box, etc."
            />
            {errors.addressLine1 && (
              <p className="text-red-500 text-sm mt-1">{errors.addressLine1}</p>
            )}
          </div>

          {/* City / Town */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              City / Town <span className="text-red-500">*</span>
            </label>
            <input
              ref={cityRef}
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:border-green-800 bg-white ${
                errors.city ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Your city or town"
            />
            {errors.city && (
              <p className="text-red-500 text-sm mt-1">{errors.city}</p>
            )}
          </div>

          {/* Postcode */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Postcode <span className="text-red-500">*</span>
            </label>
            <input
              ref={postcodeRef}
              type="text"
              name="postcode"
              value={formData.postcode}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:border-green-800 bg-white ${
                errors.postcode ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., SW1A 1AA"
            />
            {errors.postcode && (
              <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>
            )}
          </div>

          {/* Country */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Country <span className="text-red-500">*</span>
            </label>
            <select
              ref={countryRef}
              name="country"
              value={formData.country}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:border-green-800 bg-white ${
                errors.country ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select a country</option>
              {countryOptions.map((country) => (
                <option key={country.value} value={country.label}>
                  {country.label}
                </option>
              ))}
            </select>
            {errors.country && (
              <p className="text-red-500 text-sm mt-1">{errors.country}</p>
            )}
          </div>

          {/* I am a */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              I am a <span className="text-red-500">*</span>
            </label>
            <select
              ref={userTypeRef}
              name="userType"
              value={formData.userType}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:border-green-800 bg-white ${
                errors.userType ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select an option</option>
              {userTypeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.userType && (
              <p className="text-red-500 text-sm mt-1">{errors.userType}</p>
            )}
          </div>

          {/* Area of Interest */}
          <div className="md:col-span-2">
            <label className="block text-gray-700 font-semibold mb-2">
              Area of Interest <span className="text-red-500">*</span>
            </label>
            <textarea
              ref={areaOfInterestRef}
              name="areaOfInterest"
              value={formData.areaOfInterest}
              onChange={handleChange}
              rows="3"
              className={`w-full p-3 border rounded-lg focus:outline-none focus:border-green-800 bg-white ${
                errors.areaOfInterest ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Please tell us about your areas of interest, subjects, or any specific requirements..."
            />
            {errors.areaOfInterest && (
              <p className="text-red-500 text-sm mt-1">{errors.areaOfInterest}</p>
            )}
          </div>
        </div>

        {/* Register Button */}
        <div className="mt-8 text-left">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`bg-[#5c3e21] text-white px-8 py-3 rounded-lg font-bold text-lg hover:bg-[#8c5021] transition shadow-md ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Submitting...' : 'Register your Interest'}
          </button>
        </div>
      </form>

      <div className="text-left mt-6">
        <button
          onClick={() => navigate('/')}
          className="text-green-800 hover:underline font-semibold"
        >
          ← Back to Home
        </button>
      </div>
    </div>
  );
};

export default RegistrationForm;